import { organizationCountryCode, organizationCountryName } from "./countries";
import {
  australianCharityRecordUrl,
  normalizeOrganizationId,
  registryNames,
  supportsOrganizationLookup,
} from "@sfc/organization-lookup/registries";
import { normalizeEin } from "@sfc/organization-lookup/ein";
import {
  organizationAnswerSchema,
  type Question,
  type OrganizationAnswer,
} from "./types";
import type { QuestionTypeDefinition } from "./registry";

export function isUsOrganizationCountry(country: string): boolean {
  return organizationCountryCode(country) === "US";
}

/** Keep canonical lookup IDs separate from the familiar EIN display format. */
export function formatOrganizationTaxId(
  answer: Pick<OrganizationAnswer, "country" | "taxId">,
): string {
  const ein = isUsOrganizationCountry(answer.country)
    ? normalizeEin(answer.taxId)
    : null;
  return ein ? `${ein.slice(0, 2)}-${ein.slice(2)}` : answer.taxId;
}

export const ORGANIZATION_CATEGORIES = {
  "non-profit": {
    label: "Charity / nonprofit",
    description:
      "Enter the charity that would receive the grant. If your project uses a fiscal sponsor, enter that charity’s details.",
    evidence:
      "Link to the charity’s registry record or documentation of its charitable status.",
    lookup: true,
    expectedClassification: "501c3",
  },
  "social-welfare": {
    label: "Social welfare organization",
    description:
      "Enter the social welfare organization that would receive the grant. For U.S. recipients, this means a 501(c)(4) organization; elsewhere, provide its local classification in the supporting documentation.",
    evidence:
      "Link to the organization’s registry record or documentation of its social welfare status.",
    lookup: true,
    expectedClassification: "501c4",
  },
  "for-profit": {
    label: "For-profit company",
    description:
      "Enter the company that would receive the grant. Company details are entered manually; the U.S. nonprofit lookup does not cover ordinary for-profit companies.",
    evidence:
      "Link to the company’s registration record or documentation of its legal identity.",
    lookup: false,
    expectedClassification: undefined,
  },
} as const;
export type OrganizationCategory = keyof typeof ORGANIZATION_CATEGORIES;

/** Missing settings preserve existing nonprofit questions and published versions. */
export function organizationCategoryOf(
  question: Pick<Question, "config">,
): OrganizationCategory {
  return question.config?.organizationCategory ?? "non-profit";
}

export function organizationSettingsOf(question: Pick<Question, "config">) {
  return ORGANIZATION_CATEGORIES[organizationCategoryOf(question)];
}

export const organizationDefinition: QuestionTypeDefinition = {
  type: "organization",
  label: "Organization",
  hint: "Collect a grant recipient, with category-specific guidance and lookup.",
  defaultConfig: { organizationCategory: "non-profit" },
  validators: [],
  answerSchema: organizationInputSchema,
  toText: (value) =>
    value?.kind === "organization"
      ? [
          `Organization: ${value.name}`,
          `Country: ${organizationCountryName(value.country)}`,
          `Tax ID: ${formatOrganizationTaxId(value)}`,
          `Supporting link: ${value.evidenceUrl}`,
          ...(value.lookup
            ? [
                `Applicant-confirmed ${registryNames[value.lookup.source]} lookup retrieved: ${value.lookup.retrievedAt}`,
              ]
            : []),
        ].join("\n")
      : "",
};

function organizationInputSchema() {
  return organizationAnswerSchema.superRefine((value, ctx) => {
    for (const [key, label] of [
      ["country", "Country"],
      ["taxId", "Tax ID"],
      ["name", "Organization name"],
      ["evidenceUrl", "Supporting link"],
    ] as const) {
      if (!value[key].trim())
        ctx.addIssue({
          code: "custom",
          path: [key],
          message: `${label} is required when providing an organization.`,
        });
    }
    if (
      isUsOrganizationCountry(value.country) &&
      value.taxId.trim() &&
      !normalizeEin(value.taxId)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["taxId"],
        message: "Enter a nine-digit EIN, such as 12-3456789.",
      });
    }
    if (
      value.lookup &&
      (value.lookup.source === "propublica"
        ? !isUsOrganizationCountry(value.country) ||
          normalizeEin(value.taxId) !== value.lookup.ein ||
          value.evidenceUrl !==
            `https://projects.propublica.org/nonprofits/organizations/${Number(value.lookup.ein)}`
        : organizationCountryCode(value.country) !== "AU" ||
          normalizeOrganizationId("AU", value.taxId) !== value.lookup.taxId ||
          value.evidenceUrl !== australianCharityRecordUrl(value.lookup.taxId))
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["lookup"],
        message:
          "The saved lookup no longer matches this recipient. Look up the identifier again or enter details manually.",
      });
    }
    if (value.evidenceUrl.trim()) {
      try {
        const url = new URL(value.evidenceUrl.trim());
        if (!["http:", "https:"].includes(url.protocol))
          throw new Error("Invalid scheme");
      } catch {
        ctx.addIssue({
          code: "custom",
          path: ["evidenceUrl"],
          message: "Enter an http or https supporting link.",
        });
      }
    }
  });
}

/** An identifier edit must not retain details or evidence for the old recipient. */
export function changeOrganizationIdentity(
  answer: OrganizationAnswer,
  field: "country" | "taxId",
  value: string,
): OrganizationAnswer {
  const normalize = (text: string) =>
    field === "country"
      ? (organizationCountryCode(text) ?? text.trim().toLowerCase())
      : (normalizeOrganizationId(
          organizationCountryCode(answer.country) ?? "",
          text,
        ) ?? text.trim());
  return normalize(answer[field]) === normalize(value)
    ? { ...answer, [field]: value }
    : {
        ...answer,
        [field]: value,
        name: "",
        evidenceUrl: "",
        lookup: undefined,
      };
}

/** Once an identity is entered, leaving the question also flags unfinished details. */
export function organizationFieldIssues(
  answer: OrganizationAnswer,
  mode: "complete" | "blur" = "complete",
): Partial<Record<keyof OrganizationAnswer, string[]>> {
  const countryCode = organizationCountryCode(answer.country) ?? "";
  const hasIdentity = !!(
    answer.country.trim() &&
    (supportsOrganizationLookup(countryCode)
      ? normalizeOrganizationId(countryCode, answer.taxId)
      : answer.taxId.trim())
  );
  const parsed = organizationInputSchema().safeParse(answer);
  const issues: Partial<Record<keyof OrganizationAnswer, string[]>> = {};
  if (!parsed.success)
    for (const issue of parsed.error.issues) {
      const field = issue.path[0] as keyof OrganizationAnswer;
      const value = answer[field];
      if (
        mode === "blur" &&
        !(hasIdentity && (field === "name" || field === "evidenceUrl")) &&
        (value === undefined || (typeof value === "string" && !value.trim()))
      )
        continue;
      (issues[field] ??= []).push(issue.message);
    }
  return issues;
}
